import * as Color from 'color';
import {DateTime} from 'luxon';

import {contrastColor} from '@pinecast/common/color';
import {defineMessages, useIntl} from '@pinecast/i18n';
import Duration from '@pinecast/common/Duration';
import {useCSS} from '@pinecast/styles';

import {Episode} from './models';
import {DEFAULT_FONT} from '@pinecast/common/constants';
import {useTheme} from './ThemeProvider';

const messages = defineMessages({
  playing: {
    id: 'pl-spa.PlaylistItem.playing',
    description: 'Label for playing track',
    defaultMessage: 'Now playing',
  },
});

type Props = {
  episode: Episode;
  onSelect: () => void;
  selected: boolean;
};

const PlaylistItem = ({episode, onSelect, selected}: Props) => {
  const css = useCSS();
  const intl = useIntl();

  const theme = useTheme();

  const textColor = contrastColor(theme.secondaryColor);
  return (
    <button
      className={css(
        {
          appearance: 'none',
          border: 0,
          backgroundColor: 'rgba(255, 255, 255, 0)',
          borderRadius: '4px',
          color: textColor,
          cursor: 'pointer',
          display: 'grid',
          fontFamily: DEFAULT_FONT,
          gap: '2px',
          gridTemplateColumns: '1fr max-content',
          padding: '6px',
          position: 'relative',
          textAlign: 'left',
          transition: 'background-color 0.2s',
          ':not(:first-child)::after': {
            content: '""',
            height: '1px',
            left: '6px',
            right: '6px',
            backgroundColor: 'transparent',
            transition: 'background-color 0.2s',
            top: 0,
            position: 'absolute',
          },
          ':not(:hover) + .PlaylistItem:not(:hover)::after': {
            backgroundColor: Color(theme.secondaryColor).isDark()
              ? 'rgba(255, 255, 255, 0.2)'
              : 'rgba(0, 0, 0, 0.2)',
          },
          ':hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
          },
        },
        'PlaylistItem',
      )}
      onClick={e => {
        e.preventDefault();
        onSelect();
      }}
      type="button"
    >
      <div
        className={css({
          fontSize: '11px',
          lineHeight: '1em',
          textTransform: 'uppercase',
        })}
      >
        {DateTime.fromISO(episode.publish).toLocaleString({
          month: 'long',
          day: 'numeric',
        })}
      </div>
      <Duration
        className={css({fontSize: '11px', gridArea: '1 / 2 / 3 / 2'})}
        seconds={episode.audioDuration}
      />
      <div
        className={css({fontSize: '14px', fontWeight: 500, display: 'flex'})}
      >
        <div
          aria-hidden={!selected}
          className={css({
            alignSelf: 'flex-start',
            color: textColor,
            flex: `0 0 ${selected ? '1em' : '0em'}`,
            height: '1em',
            marginTop: '0.15em',
            width: selected ? '1em' : '0em',
            opacity: 0.75,
            overflow: 'hidden',
            marginRight: selected ? '4px' : '0px',
            transition: 'margin-right 0.2s, width 0.2s, flex-basis 0.2s',
          })}
        >
          <svg
            aria-label={intl.formatMessage(messages.playing)}
            viewBox="0 0 16 16"
            className={css({height: '1em', width: '1em'})}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M14 0H2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V2a2 2 0 00-2-2zm-3 8.75l-4.58 2.57a.86.86 0 01-1.28-.75V5.43a.86.86 0 011.28-.74L11 7.26a.86.86 0 010 1.5z"
              fill="currentColor"
            />
          </svg>
        </div>
        <span>{episode.title}</span>
      </div>
    </button>
  );
};

export default PlaylistItem;
